import { useState, useEffect } from 'react';
import { Heart, Play, Pause, CheckCircle } from 'lucide-react';
import { Button } from '../ui';

export function CardioWarmup({ onComplete, onExit }) {
  const [timerStarted, setTimerStarted] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(10 * 60); // 10 minutes

  // Timer logic
  useEffect(() => {
    let interval = null;

    if (timerStarted && !timerPaused && secondsRemaining > 0) {
      interval = setInterval(() => {
        setSecondsRemaining(prev => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerStarted, timerPaused, secondsRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    setTimerStarted(true);
    setTimerPaused(false);
  };

  const handlePauseTimer = () => {
    setTimerPaused(!timerPaused);
  };

  const progressPercent = ((10 * 60 - secondsRemaining) / (10 * 60)) * 100;

  return (
    <div className="flex-1 flex flex-col p-4">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto text-center">
        {/* Icon */}
        <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <Heart className="h-10 w-10 text-red-500" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Cardio Warmup
        </h2>

        {/* Instructions */}
        <p className="text-gray-600 mb-8">
          Complete 10 minutes of cardio on your equipment of choice 
          (e.g., treadmill, elliptical, stationary bike, rowing ergometer).
        </p>

        {/* Timer Display */}
        {timerStarted && (
          <div className="w-full mb-8">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="h-2 rounded-full bg-primary transition-all duration-1000"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Time */}
            <div className="text-4xl font-mono font-bold text-gray-900 mb-2">
              {formatTime(secondsRemaining)}
            </div>
            <p className="text-sm text-gray-500">remaining</p>
          </div>
        )}

        {/* Timer Controls */}
        {!timerStarted ? (
          <Button onClick={handleStartTimer} size="lg">
            <Play className="h-5 w-5 mr-2" />
            Start Timer
          </Button>
        ) : secondsRemaining > 0 ? (
          <Button 
            variant="outline" 
            onClick={handlePauseTimer}
            size="lg"
          >
            {timerPaused ? (
              <>
                <Play className="h-5 w-5 mr-2" />
                Resume
              </>
            ) : (
              <>
                <Pause className="h-5 w-5 mr-2" />
                Pause
              </>
            )}
          </Button>
        ) : (
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="mt-auto pt-4 space-y-3">
        <Button
          onClick={onComplete}
          fullWidth
          size="lg"
        >
          {secondsRemaining === 0 ? 'Continue to Exercises' : 'Skip & Continue'}
        </Button>

        <p className="text-xs text-center text-gray-500">
          The timer is optional - continue when you're ready
        </p>
      </div>
    </div>
  );
}